import React, { useState, useEffect } from 'react';
import api from '../api';
import toast, { Toaster } from 'react-hot-toast';
import ExportButton from '../components/ExportButton';
import { useAuth } from '../context/AuthContext';
import { todayLocalStr, firstOfMonthLocalStr } from '../utils/dateUtils';

export default function Expenses() {
  const { user } = useAuth();
  const isCollector = user && user.role === 'collector';
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [catId, setCatId] = useState('');
  const [acctId, setAcctId] = useState('');
  const [amount, setAmount] = useState('');
  const [desc, setDesc] = useState('');
  const [userId, setUserId] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [expDate, setExpDate] = useState(todayLocalStr());
  const [startDate, setStartDate] = useState(firstOfMonthLocalStr());
  const [endDate, setEndDate] = useState(todayLocalStr());
  // Click-to-sort, as requested.
  const [sort, setSort] = useState({ key: 'expense_date', dir: 'desc' });

  useEffect(() => {
    fetchExpenses();
    fetchCategories();
    fetchAccounts(); api.get('/auth/users').then(r => setUsersList(r.data));
  }, []);

  const fetchExpenses = () => {
    // Fixes the requested behavior: a collector opening Expenses should
    // only ever see the expenses specifically linked to them (e.g. an
    // advance/salary entry an admin tagged with their name) — never the
    // whole company's expense list. This is enforced here regardless of
    // what's in the UI, and the server also independently applies whatever
    // user_id is passed.
    var url = '/expenses?startDate=' + startDate + '&endDate=' + endDate;
    if (isCollector) url += '&user_id=' + user.id;
    api.get(url).then(r => setExpenses(r.data));
  };
  const fetchCategories = () => api.get('/expenses/categories').then(r => setCategories(r.data));
  const fetchAccounts = () => api.get('/accounts').then(r => setAccounts(r.data));

  function handleSort(key) {
    setSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  }
  function sortIndicator(key) {
    if (sort.key !== key) return '';
    return sort.dir === 'asc' ? ' \u25B2' : ' \u25BC';
  }
  const sortedExpenses = expenses.slice().sort((a, b) => {
    let av = a[sort.key], bv = b[sort.key];
    if (sort.key === 'amount') { av = av || 0; bv = bv || 0; }
    else { av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase(); }
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  const addCategory = (e) => {
    e.preventDefault();
    if (!newCat.trim()) return toast.error('Category name required');
    api.post('/expenses/categories', { name: newCat }).then(() => {
      toast.success('Category added!');
      setNewCat(''); setShowCatForm(false); fetchCategories();
    }).catch(err => toast.error(err.response?.data?.error || 'Failed'));
  };

  const addExpense = (e) => {
    e.preventDefault();
    if (!catId || !acctId || !amount) return toast.error('Fill required fields');
    api.post('/expenses', {
      category_id: parseInt(catId), account_id: parseInt(acctId),
      amount: parseFloat(amount), description: desc, expense_date: expDate, user_id: userId || null
    }).then(() => {
      toast.success('Expense recorded!');
      setShowForm(false); setCatId(''); setAcctId(''); setAmount(''); setDesc(''); setUserId('');
      fetchExpenses(); fetchAccounts(); api.get('/auth/users').then(r => setUsersList(r.data));
    }).catch(err => toast.error(err.response?.data?.error || 'Failed'));
  };

  const fmt = (n) => '₹' + (n || 0).toLocaleString('en-IN');
  const totalExpenses = expenses.reduce((s, e) => s + e.amount, 0);

  return (
    <div>
      <Toaster position="top-right" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <h2 style={{ fontSize: 24, fontWeight: 'bold', margin: 0 }}>{isCollector ? 'My Expenses' : 'Expenses'}</h2>
        {!isCollector && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setShowCatForm(true)} style={{ background: '#4b5563', color: 'white', padding: '8px 16px', border: 'none', borderRadius: 8, cursor: 'pointer' }}>+ Category</button>
            <button onClick={() => setShowForm(true)} style={{ background: '#dc2626', color: 'white', padding: '8px 16px', border: 'none', borderRadius: 8, cursor: 'pointer' }}>+ Add Expense</button><ExportButton apiUrl="/api/expenses" filename="expenses.csv" /></div>
        )}
      </div>

      {/* Date Filter */}
      <div style={{ background: 'white', borderRadius: 8, padding: 12, marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end' }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 2 }}>From</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 2 }}>To</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
        </div>
        <button onClick={fetchExpenses} style={{ background: '#2563eb', color: 'white', border: 'none', padding: '7px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Apply</button>
        <div style={{ marginLeft: 'auto', fontSize: 16, fontWeight: 600, color: '#dc2626' }}>
          Total: {fmt(totalExpenses)}
        </div>
      </div>

      {/* Category Modal */}
      {showCatForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '100%', maxWidth: 400 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Add Expense Category</h3>
            <form onSubmit={addCategory}>
              <input type="text" value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="e.g. Salary, Electricity Bill" style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box', marginBottom: 12 }} autoFocus />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" style={{ flex: 1, padding: 10, background: '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Save</button>
                <button type="button" onClick={() => setShowCatForm(false)} style={{ flex: 1, padding: 10, background: '#e5e7eb', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showForm && (
        <div style={{ background: 'white', borderRadius: 8, padding: 24, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>Record Expense</h3>
          <form onSubmit={addExpense}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Category *</label>
                <select value={catId} onChange={e => setCatId(e.target.value)} required style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }}>
                  <option value="">Select</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Account *</label>
                <select value={acctId} onChange={e => setAcctId(e.target.value)} required style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }}>
                  <option value="">Select</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({fmt(a.balance)})</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Amount *</label>
                <input type="number" value={amount} onChange={e => setAmount(e.target.value)} required min="1" style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Date</label>
                <input type="date" value={expDate} onChange={e => setExpDate(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Description</label>
              <input type="text" value={desc} onChange={e => setDesc(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Link to User (optional)</label>
              <select value={userId} onChange={e => setUserId(e.target.value)} style={{ width: '100%', padding: 8, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }}>
                <option value="">None — company expense</option>
                {usersList.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
              </select>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                Link this to a staff member (e.g. an advance/salary payment) and only they will see it under their own Expenses. Leave as "None" for a normal company expense.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" style={{ flex: 1, padding: 10, background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>Record Expense</button>
              <button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, padding: 10, background: '#e5e7eb', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Expenses Table */}
      <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {[
                { key: 'expense_date', label: 'Date' },
                { key: 'category_name', label: 'Category' },
                { key: 'description', label: 'Description' },
                { key: 'account_name', label: 'Account' },
                { key: 'user_name', label: 'User' },
              ].map(col => (
                <th key={col.key} onClick={() => handleSort(col.key)} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 500, color: sort.key === col.key ? '#2563eb' : '#6b7280', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>{col.label}{sortIndicator(col.key)}</th>
              ))}
              <th onClick={() => handleSort('amount')} style={{ padding: '10px 16px', textAlign: 'right', fontSize: 12, fontWeight: 500, color: sort.key === 'amount' ? '#2563eb' : '#6b7280', cursor: 'pointer', userSelect: 'none' }}>Amount{sortIndicator('amount')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedExpenses.map(exp => (
              <tr key={exp.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: '10px 16px' }}>{new Date(exp.expense_date).toLocaleDateString('en-IN')}</td>
                <td style={{ padding: '10px 16px' }}><span style={{ padding: '3px 10px', background: '#f3f4f6', borderRadius: 12, fontSize: 13 }}>{exp.category_name}</span></td>
                <td style={{ padding: '10px 16px' }}>{exp.description || '-'}</td>
                <td style={{ padding: '10px 16px' }}>{exp.account_name}</td><td style={{ padding: '10px 16px', fontSize: 13 }}>{exp.user_name || '-'}</td>
                <td style={{ padding: '10px 16px', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>{fmt(exp.amount)}</td>
              </tr>
            ))}
            {expenses.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 32, color: '#6b7280' }}>No expenses recorded</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}





